import React, { useState, useMemo } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { useApp } from '../../context/AppContext';
import KPICard from '../../components/KPICard/KPICard';
import Card from '../../components/Card/Card';
import { MROTypeBadge } from '../../components/Badge/Badge';
import '../../styles/layout.css';
import '../../styles/tables.css';

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const TYPE_COLORS = {
  'C-Check':     '#1246d6',
  'D-Check':     '#7c3aed',
  'Engine Shop': '#d97706',
  'Line Maint':  '#16a34a',
  'Repaint':     '#0698a0',
  'Storage':     '#dc2626',
  'Base':        '#1246d6',
  'Line + A':    '#7c3aed',
  'Paint':       '#ea580c',
  'Other':       '#8faabb',
};

const doughnutOpts = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: '65%',
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: (ctx) =>
          ` ${ctx.label}: ${ctx.parsed} (${Math.round(
            (ctx.parsed / ctx.dataset.data.reduce((a, b) => a + b, 0)) * 100
          )}%)`,
      },
    },
  },
};

const hBarOpts = {
  indexAxis: 'y',
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    x: {
      grid: { color: '#f0f3f8' },
      ticks: { font: { size: 9.5 }, color: '#8faabb' },
    },
    y: {
      grid: { display: false },
      ticks: { font: { size: 9 }, color: '#3d5166' },
    },
  },
};

function DonutLegend({ labels, data, colors }) {
  const total = data.reduce((a, b) => a + b, 0) || 1;
  return (
    <div className="dleg">
      {labels.map((l, i) => (
        <div className="dlr" key={l}>
          <div className="dll">
            <div className="dls" style={{ background: colors[i] }} />
            <span style={{ color: 'var(--tx2)' }}>{l}</span>
          </div>
          <span className="dln">
            {data[i]}{' '}
            <span style={{ color: 'var(--tx3)', fontSize: 9 }}>
              {Math.round((data[i] / total) * 100)}%
            </span>
          </span>
        </div>
      ))}
    </div>
  );
}

export default function MRO() {
  const { operator, D } = useApp();
  const [search, setSearch] = useState('');

  const mro = useMemo(() => {
    let m = D.mro || [];
    if (operator && operator !== 'All') {
      const regs = new Set((D.fleet || []).filter((a) => a.O === operator).map((a) => a.R));
      m = m.filter((e) => regs.has(e.regIn));
    }
    return m;
  }, [D, operator]);

  const filtered = useMemo(() => {
    if (!search.trim()) return mro;
    const q = search.toLowerCase();
    return mro.filter(
      (e) =>
        (e.regIn || '').toLowerCase().includes(q) ||
        (e.varIn || '').toLowerCase().includes(q) ||
        (e.opIn || '').toLowerCase().includes(q) ||
        (e.facility || '').toLowerCase().includes(q) ||
        (e.type || '').toLowerCase().includes(q) ||
        (e.country || '').toLowerCase().includes(q)
    );
  }, [mro, search]);

  const kpi = useMemo(() => {
    const events = mro.length;
    const avgDays =
      mro.length
        ? Math.round(mro.reduce((s, e) => s + e.days, 0) / mro.length) + ' days'
        : '—';
    const facilities = new Set(mro.map((e) => e.facility)).size;
    const countries = new Set(mro.map((e) => e.country)).size;
    return { events, avgDays, facilities, countries };
  }, [mro]);

  const typeChartData = useMemo(() => {
    const counts = {};
    mro.forEach((e) => {
      const t = e.type || 'Other';
      counts[t] = (counts[t] || 0) + 1;
    });
    const labels = Object.keys(counts);
    const data = labels.map((l) => counts[l]);
    const colors = labels.map((l) => TYPE_COLORS[l] || TYPE_COLORS['Other']);
    return { labels, data, colors };
  }, [mro]);

  const facilityChartData = useMemo(() => {
    const counts = {};
    mro.forEach((e) => {
      const f = e.facility || 'Unknown';
      counts[f] = (counts[f] || 0) + 1;
    });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 8);
    const labels = sorted.map(([f]) => f);
    const data = sorted.map(([, c]) => c);
    return { labels, data };
  }, [mro]);

  return (
    <div className="page-content">

      <div className="krow krow-4">
        <KPICard
          label="MRO Events"
          value={kpi.events}
          sub="Filtered events"
          accent="kb"
        />
        <KPICard
          label="Avg Duration"
          value={kpi.avgDays}
          sub="Per event"
          accent="kt"
        />
        <KPICard
          label="Facilities"
          value={kpi.facilities}
          sub="Active shops"
          accent="kp"
        />
        <KPICard
          label="Countries"
          value={kpi.countries}
          sub="Geographies"
          accent="kg"
        />
      </div>

      <div className="g2">

        <Card title="MRO by Type" icon="🔩" iconClass="ib">
          <div className="cwsm">
            <Doughnut
              data={{
                labels: typeChartData.labels,
                datasets: [{ data: typeChartData.data, backgroundColor: typeChartData.colors, borderWidth: 0 }],
              }}
              options={doughnutOpts}
            />
          </div>
          <DonutLegend
            labels={typeChartData.labels}
            data={typeChartData.data}
            colors={typeChartData.colors}
          />
        </Card>

        <Card title="MRO Events by Facility" icon="🏭" iconClass="it">
          <div className="cwsm">
            <Bar
              data={{
                labels: facilityChartData.labels,
                datasets: [{
                  label: 'Events',
                  data: facilityChartData.data,
                  backgroundColor: '#1246d6',
                  borderRadius: 3,
                  borderSkipped: false,
                }],
              }}
              options={hBarOpts}
            />
          </div>
        </Card>

      </div>

      <Card
        title="MRO Event Log"
        icon="🔧"
        iconClass="it"
        noPad
        headerRight={
          <div className="sw">
            <span className="si">🔍</span>
            <input
              className="sinp"
              placeholder="Search MRO events…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        }
      >
        <div className="ts full">
          <table>
            <thead>
              <tr>
                <th>Registration</th>
                <th>MSN</th>
                <th>Variant</th>
                <th>Type</th>
                <th>Operator</th>
                <th>Facility</th>
                <th>Airport</th>
                <th>Country</th>
                <th>Date In</th>
                <th>Date Out</th>
                <th>Duration</th>
                <th>Age</th>
                <th>Engine</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e, i) => (
                <tr key={i}>
                  <td>{e.regIn || '—'}</td>
                  <td>{e.msn || '—'}</td>
                  <td>{e.varIn || '—'}</td>
                  <td><MROTypeBadge type={e.type} /></td>
                  <td>{e.opIn || '—'}</td>
                  <td>{e.facility || '—'}</td>
                  <td>{e.airport || '—'}</td>
                  <td>{e.country || '—'}</td>
                  <td>{e.dateIn || '—'}</td>
                  <td>{e.dateOut || '—'}</td>
                  <td>{e.days != null ? `${e.days} d` : '—'}</td>
                  <td>{e.age != null ? `${e.age} yrs` : '—'}</td>
                  <td>{e.eng || '—'}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={13} style={{ textAlign: 'center', color: 'var(--tx3)', padding: '24px 0' }}>
                    No MRO events found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

    </div>
  );
}
